import { useState } from 'react';
import { getAuth } from 'firebase/auth';

const API_URL = 'https://cognidoor-backend-92219505867.us-east1.run.app/api/ecosystems/solve';

// Helper: Normalize a string for comparison (match backend logic)
const normalize = (name) => {
  if (typeof name !== 'string') {
    return String(name || '').toLowerCase().trim();
  }
  return name.toLowerCase().trim();
};

// Helper function to simulate feeding process
const simulateFeeding = (producers, animals) => {
  // Build a dictionary of all species in the chosen combination
  const state = {};
  
  // Record producers
  for (const p of producers) {
    state[normalize(p.Name)] = {
      type: 'producer',
      current: p['Calories Provided']
    };
  }
  
  // For animals, allowed food sources are the ones from the animal's food_sources
  const chosenNames = new Set([
    ...producers.map(p => normalize(p.Name)),
    ...animals.map(a => normalize(a.Name))
  ]);
  for (const a of animals) {
    // Normalize food sources (match backend logic)
    const foodSourcesRaw = a['Food Sources'] || a.foodSources || [];
    const foodSourcesArray = Array.isArray(foodSourcesRaw) ? foodSourcesRaw : [foodSourcesRaw];
    const foodSourcesNormalized = foodSourcesArray.map(fs => normalize(String(fs || '')));
    const allowed = foodSourcesNormalized.filter(fs => chosenNames.has(fs));
    state[normalize(a.Name)] = {
      type: 'animal',
      current: a['Calories Provided'] ?? a.caloriesProvided,
      needed: a['Calories Needed'] ?? a.caloriesNeeded,
      allowed,
      hasEaten: false
    };
    // Debug log for allowed food sources
    // console.log(`[SOLVER DEBUG] Animal "${normalize(a.Name)}" allowed food sources:`, allowed);
  }
  
  // Debug: Print state before simulation
  // console.log('[SOLVER DEBUG] Initial state:', JSON.parse(JSON.stringify(state)));
  
  // Make a working copy of state
  const workingState = JSON.parse(JSON.stringify(state));
  
  // List of animals that have not yet eaten
  const animalsToFeed = animals.map(a => normalize(a.Name));
  
  // Simulation Loop
  while (animalsToFeed.length > 0) {
    // Choose the animal with the highest current calories provided
    const predatorName = animalsToFeed.reduce((max, name) => 
      workingState[name].current > workingState[max].current ? name : max
    );
    const predator = workingState[predatorName];
    const required = predator.needed;
    
    // Find allowed food sources
    const allowedSources = predator.allowed.filter(src => src in workingState);
    if (allowedSources.length === 0) {
      // console.log(`[SOLVER DEBUG] Animal "${predatorName}" has no available food sources. Failing.`);
      return false;
    }
    
    // Determine the maximum current calories among allowed food sources
    const maxCurrent = Math.max(...allowedSources.map(src => workingState[src].current));
    const topSources = allowedSources.filter(src => workingState[src].current === maxCurrent);
    
    if (topSources.length > 1) {
      // Splitting: predator divides its required calories equally
      const splitAmount = required / topSources.length;
      // Check that each source has more than the split amount
      for (const src of topSources) {
        if (workingState[src].current - splitAmount <= 0) {
          // console.log(`[SOLVER DEBUG] Source "${src}" cannot provide enough calories for split. Failing.`);
          return false;
        }
      }
      // Subtract the split amount from each top source
      for (const src of topSources) {
        workingState[src].current -= splitAmount;
      }
    } else {
      // Only one top source
      const src = topSources[0];
      if (workingState[src].current - required <= 0) {
        // console.log(`[SOLVER DEBUG] Source "${src}" cannot provide enough calories for full need. Failing.`);
        return false;
      }
      workingState[src].current -= required;
    }
    
    // Mark predator as having eaten
    predator.needed = 0;
    predator.hasEaten = true;
    animalsToFeed.splice(animalsToFeed.indexOf(predatorName), 1);
    // console.log(`[SOLVER DEBUG] Fed animal "${predatorName}". State after feeding:`, JSON.parse(JSON.stringify(workingState)));
  }
  
  // Final Check: Ensure every species has current > 0
  const allSurvive = Object.values(workingState).every(species => species.current > 0);
  if (!allSurvive) {
    // console.log('[SOLVER DEBUG] Not all species survived. Failing.');
  } else {
    // console.log('[SOLVER DEBUG] All species survived!');
  }
  // console.log('[SOLVER DEBUG] All species survived!');
  return allSurvive;
};

// Helper function to select sustainable animals
const selectSustainableAnimals = (producers, allAnimals) => {
  // Generate all possible combinations of 5 animals
  const combinations = [];
  const generateCombinations = (current, start) => {
    if (current.length === 5) {
      combinations.push([...current]);
      return;
    }
    for (let i = start; i < allAnimals.length; i++) {
      current.push(allAnimals[i]);
      generateCombinations(current, i + 1);
      current.pop();
    }
  };
  generateCombinations([], 0);
  
  // Test each combination
  for (const animalCombo of combinations) {
    // console.log('[SOLVER DEBUG] Testing animal combo:', animalCombo.map(a => a.Name));
    if (simulateFeeding(producers, animalCombo)) {
      return animalCombo;
    }
  }
  return null;
};

export const useEcosystemSolver = () => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const solve = async (data) => {
    try {
      setLoading(true);
      setError(null);
      
      // Transform the data to match the API format
      const apiInput = {
        ecosystem_name: data.ecosystemName,
        min_depth: data.depthTemperature.minDepth,
        max_depth: data.depthTemperature.maxDepth,
        min_temp: data.depthTemperature.minTemp,
        max_temp: data.depthTemperature.maxTemp,
        producers: data.producers.map(p => ({
          Name: p.name,
          'Calories Provided': p.calories,
          'Eaten By': Array.isArray(p.eatenBy) ? p.eatenBy : (p.eatenBy || '').split(',').map(s => s.trim())
        })),
        animals: data.animals.map(a => ({
          Name: a.name,
          'Calories Needed': a.caloriesNeeded,
          'Calories Provided': a.caloriesProvided,
          'Food Sources': Array.isArray(a.foodSources) ? a.foodSources : (a.foodSources || '').split(',').map(s => s.trim())
        }))
      };
      
      // First try to solve locally using the algorithm
      const chosenAnimals = selectSustainableAnimals(apiInput.producers, apiInput.animals);
      
      if (chosenAnimals) {
        // If we found a solution locally, use it
        const transformedResult = {
          ecosystemName: apiInput.ecosystem_name,
          sustainable: true,
          chosenAnimals: chosenAnimals.map(a => a.Name),
          producers: apiInput.producers.map(p => p.Name),
          depthTemperature: `${apiInput.min_depth}-${apiInput.max_depth}m, ${apiInput.min_temp}-${apiInput.max_temp}°C`,
          error: null
        };
        setResult(transformedResult);
      } else {
        // If no local solution, try the API
        const auth = getAuth();
        const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
        const response = await fetch(API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(apiInput),
        });

        if (!response.ok) {
          throw new Error(`Solver request failed with status ${response.status}`);
        }

        const responseData = await response.json();
        
        // Transform the response back to frontend format
        const transformedResult = {
          ecosystemName: responseData.ecosystem_name,
          sustainable: responseData.sustainability === 'sustainable',
          chosenAnimals: responseData.selected_animals,
          producers: responseData.selected_producers,
          depthTemperature: responseData.selected_depth_temperature,
          error: responseData.sustainability === 'sustainable' ? null : 'No sustainable combination of animals was found.'
        };
        setResult(transformedResult);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  return { solve, result, loading, error };
}; 
